// Angular
import { Injectable } from '@angular/core';

// Libraries
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { append, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

// Services
import { AuthService } from '../../auth';
import { UserService } from '../services/user.service';
import { CompanyService } from '../../company/services/company.service';
import { CoreService } from '../../../shared/services/core.service';
import { SpinnerService } from '../../../shared/components/spinner/services/spinner.service';

// Models
import { AppCleanup, UserModel } from '../../../shared/models';

// Store
import { UserActions } from './user.action';

// Enums
import { StatusType } from '../../../shared/enums';
import { ConfirmDialogModel } from '../../../shared/dialogs/confirm-dialog/confirm-dialog.component';
import { UtilityHelper } from '../../../shared/helpers/utility.helper';
import { DialogService } from '../../../shared/services/dialog.service';

export interface UserStateModel {
	items: UserModel[];
	loaded: boolean;
}

const defaultState: UserStateModel = {
	items: undefined,
	loaded: false,
};

@State<any>({
	name: 'user',
	defaults: defaultState,
})
@Injectable()
export class UserState extends AppCleanup {
	constructor(
		private authService: AuthService,
		private userService: UserService,
		private companyService: CompanyService,
		private spinnerService: SpinnerService,
		private coreService: CoreService,
		private translateService: TranslateService,
		private dialogService: DialogService
	) {
		super();
	}

	@Action(UserActions.GetAll)
	getAll(ctx: StateContext<UserStateModel>, action: UserActions.GetAll) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.userService
			.getUsers()
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (users) => {
					ctx.setState({
						items: users,
						loaded: true,
					});

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}
				},
				error: () => {
					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.ERROR_GETTING', { name: this.translateService.instant('MODULE.USER.TITLE_PLURAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(UserActions.SetAll)
	setAll(ctx: StateContext<UserStateModel>, action: UserActions.SetAll) {
		ctx.setState({
			items: action.users,
			loaded: true,
		});
	}

	@Action(UserActions.Create)
	create(ctx: StateContext<UserStateModel>, action: UserActions.Create) {
		let throwError = () => {
			ctx.dispatch(new UserActions.CreateError());

			if (action.data.showSpinner) {
				this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
			}

			if (action.data.showToast) {
				const message = `${this.translateService.instant('GENERIC.NOT_CREATED', { name: this.translateService.instant('MODULE.USER.TITLE_CAPITAL') })}`;
				this.coreService.toast({ message: message, type: StatusType.DANGER });
			}
		};

		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.authService
			.registerExternal(action.data.user, false)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (response) => {
					if (response.errors.length > 0) {
						throwError();
						return;
					}

					ctx.setState(
						patch({
							items: append([response.user]),
						})
					);

					ctx.dispatch(new UserActions.CreateSuccess(response.user));

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.CREATED', { name: this.translateService.instant('MODULE.USER.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.SUCCESS });
					}
				},
				error: () => {
					throwError();
				},
			});
	}

	@Action(UserActions.Update)
	update(ctx: StateContext<UserStateModel>, action: UserActions.Update) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.userService
			.createUpdateUser(action.data.user)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (user) => {
					const currentState = ctx.getState();
					const existingItem = currentState.items?.find((item) => item.id === user.id);

					if (existingItem) {
						ctx.setState(
							patch({
								items: updateItem((user) => user.id === action.data.user.id, action.data.user),
							})
						);
					}

					ctx.dispatch(new UserActions.UpdateSuccess(user));

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.UPDATED', { name: this.translateService.instant('MODULE.USER.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.SUCCESS });
					}
				},
				error: () => {
					ctx.dispatch(new UserActions.UpdateError());

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.NOT_UPDATED', { name: this.translateService.instant('MODULE.USER.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(UserActions.UpdateCompanyUser)
	updateCompanyUser(ctx: StateContext<UserStateModel>, action: UserActions.UpdateCompanyUser) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.companyService
			.editUserToCompany(action.data.companyUser)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (companyUser) => {
					ctx.dispatch(new UserActions.GetAll({ showSpinner: false, showToast: false }));

					ctx.dispatch(new UserActions.UpdateCompanyUserSuccess(companyUser));

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.UPDATED', { name: this.translateService.instant('MODULE.USER.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.SUCCESS });
					}
				},
				error: () => {
					ctx.dispatch(new UserActions.UpdateCompanyUserError());

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.NOT_UPDATED', { name: this.translateService.instant('MODULE.USER.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(UserActions.UpdateCompanyUserSuccess)
	updateCompanyUserSuccess(ctx: StateContext<UserStateModel>, action: UserActions.UpdateCompanyUserSuccess) {}

	@Action(UserActions.UpdateCompanyUserError)
	updateCompanyUserError(ctx: StateContext<UserStateModel>) {}

	@Action(UserActions.Delete)
	delete(ctx: StateContext<UserStateModel>, action: UserActions.Delete) {
		let deleteUser = () => {
			if (action.data.showSpinner) {
				this.spinnerService.showAppSpinner(); // Turns on app wide spinner
			}

			return this.userService
				.deleteUser(action.data.id)
				.pipe(takeUntil(this.destroy))
				.subscribe({
					next: () => {
						const currentState = ctx.getState();
						const existingItem = currentState.items?.find((item) => item.id === action.data.id);

						if (existingItem) {
							ctx.setState(
								patch({
									items: removeItem((user) => user.id === action.data.id),
								})
							);
						}

						if (action.data.showSpinner) {
							this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
						}

						if (action.data.showToast) {
							const message = `${this.translateService.instant('GENERIC.DELETED', { name: this.translateService.instant('MODULE.USER.TITLE_CAPITAL') })}`;
							this.coreService.toast({ message: message, type: StatusType.SUCCESS });
						}
					},
					error: () => {
						if (action.data.showSpinner) {
							this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
						}

						if (action.data.showToast) {
							const message = `${this.translateService.instant('GENERIC.NOT_DELETED', { name: this.translateService.instant('MODULE.USER.TITLE_CAPITAL') })}`;
							this.coreService.toast({ message: message, type: StatusType.DANGER });
						}
					},
				});
		};

		if (action.data.showConfirm) {
			const confirmModel: ConfirmDialogModel = {
				title: `${UtilityHelper.capitalizeFirstLetter(this.translateService.instant('ACTION.DELETE'))} ${this.translateService.instant('MODULE.USER.TITLE')}`,
				message: `${this.translateService.instant('GENERIC.CONFIRM_DELETE', { name: this.translateService.instant('MODULE.USER.TITLE') })}`,
				action: this.translateService.instant('ACTION.DELETE_CAPITAL'),
			};

			const dialogRef = this.dialogService.confirmAction(confirmModel);

			dialogRef
				.afterClosed()
				.pipe(takeUntil(this.destroy))
				.subscribe((result) => {
					if (result) {
						deleteUser();
					}
				});
		} else {
			deleteUser();
		}
	}

	@Selector()
	static isLoaded(state: UserStateModel): boolean {
		return state.loaded;
	}

	@Selector()
	static users(state: UserStateModel): UserModel[] {
		return state.items;
	}
}
